import React, { useState, useEffect } from 'react';

import { bool, string, number, func } from 'prop-types';
import classNames from 'classnames';
import { Field } from 'react-final-form';

import { FormattedMessage } from '../../../util/reactIntl';
import { RangeSlider } from '../../../components';

import css from './IntegerRangeFilter.module.css';

const RADIX = 10;

//  Ensures that the given values object has both minimum and maximum values.
//  If `values` does not include `minValue` or `maxValue`, defaults are applied.
const resolveMinMaxValues = (values, defaultMax, defaultMin) => {
  const { maxValue, minValue } = values || {};
  return {
    maxValue: maxValue != null ? maxValue : defaultMax,
    minValue: minValue != null ? minValue : defaultMin,
  };
};

// Checks the pair of (possibly still-being-typed) values against the field's
// configured bounds and against each other. Returns a message descriptor for
// whichever side(s) are currently invalid, or null when a side is fine.
const validateRange = ({ minValue, maxValue }, defaultMinValue, defaultMaxValue) => {
  const minIsNumber = typeof minValue === 'number' && !isNaN(minValue);
  const maxIsNumber = typeof maxValue === 'number' && !isNaN(maxValue);

  let minError = null;
  let maxError = null;

  if (minIsNumber && defaultMinValue != null && minValue < defaultMinValue) {
    minError = { id: 'IntegerRangeFilter.minTooLow', values: { min: defaultMinValue } };
  } else if (minIsNumber && maxIsNumber && minValue > maxValue) {
    minError = { id: 'IntegerRangeFilter.minGreaterThanMax' };
  }

  if (maxIsNumber && defaultMaxValue != null && maxValue > defaultMaxValue) {
    maxError = { id: 'IntegerRangeFilter.maxTooHigh', values: { max: defaultMaxValue } };
  } else if (minIsNumber && maxIsNumber && maxValue < minValue) {
    maxError = { id: 'IntegerRangeFilter.maxLessThanMin' };
  }

  return { minError, maxError };
};

const RangeInput = props => {
  const {
    input,
    min: defaultMinValue,
    max: defaultMaxValue,
    step,
    isInSideBar,
    initialValues,
    onValidityChange,
    onValuesChange,
  } = props;
  const { onChange, name } = input;

  const currentValues = resolveMinMaxValues(initialValues[name], defaultMaxValue, defaultMinValue);
  const [fieldValues, setFieldValues] = useState(currentValues);
  const [minError, setMinError] = useState(null);
  const [maxError, setMaxError] = useState(null);
  const [activeField, setActiveField] = useState(null);


  useEffect(() => {
    setFieldValues(currentValues);
    setMinError(null);
    setMaxError(null);
    setActiveField(null);
  }, [currentValues.minValue, currentValues.maxValue]);

  useEffect(() => {
    if (onValidityChange) {
      onValidityChange(!!(minError || maxError));
    }
  }, [minError, maxError]);

  // Reports exactly what's currently shown in the min/max boxes, so the
  // submit handler can use this directly instead of final-form's tracked
  // value (which only updates while both sides are valid, and can lag).
  useEffect(() => {
    if (onValuesChange) {
      onValuesChange(fieldValues);
    }
  }, [fieldValues]);

  // Applies a parsed value to one side of the range, re-validates both sides
  // against each other and the configured bounds, and only propagates the
  // change upstream (triggering a search) once both sides are valid. Invalid
  // input is still reflected in local state so the user can keep typing/editing.
  const applyChange = (side, rawValue) => {
    setActiveField(side);

    const parsedValue = rawValue === '' ? '' : Number.parseInt(rawValue, RADIX);
    if (rawValue !== '' && isNaN(parsedValue)) {
      return;
    }

    const newValues = { ...fieldValues, [side]: parsedValue };
    setFieldValues(newValues);

    const { minError: nextMinError, maxError: nextMaxError } = validateRange(
      newValues,
      defaultMinValue,
      defaultMaxValue
    );
    setMinError(nextMinError);
    setMaxError(nextMaxError);

    if (!nextMinError && !nextMaxError && parsedValue !== '') {
      onChange(newValues);
    }
  };

  const handleMinValueChange = event => applyChange('minValue', event.target.value);
  const handleMaxValueChange = event => applyChange('maxValue', event.target.value);

  const handleSliderChange = updatedValue => {
    setFieldValues({ ...updatedValue });
    setMinError(null);
    setMaxError(null);
    setActiveField(null);
    onChange({ ...updatedValue });
  };

  const clampForSlider = value => {
    if (typeof value !== 'number' || isNaN(value)) {
      return defaultMinValue;
    }
    if (defaultMinValue != null && value < defaultMinValue) {
      return defaultMinValue;
    }
    if (defaultMaxValue != null && value > defaultMaxValue) {
      return defaultMaxValue;
    }
    return value;
  };
  const sliderLow = clampForSlider(fieldValues.minValue);
  const sliderHigh = clampForSlider(fieldValues.maxValue);
  const sliderHandles =
    sliderLow <= sliderHigh ? [sliderLow, sliderHigh] : [sliderHigh, sliderLow];

  const classes = isInSideBar ? css.formWrapper : null;

  return (
    <div className={classes}>
      <div className={classNames(css.contentWrapper, { [css.contentWrapperSidebar]: isInSideBar })}>
        <div className={css.inputsWrapper}>
          <div className={css.inputWithTooltip}>
            <input
              className={classNames(css.minValue, {
                [css.valueInSidebar]: isInSideBar,
                [css.inputError]: !!minError,
              })}
              type="number"
              step="any"
              placeholder={defaultMinValue}
              value={fieldValues.minValue}
              onChange={handleMinValueChange}
            ></input>
            {minError && activeField === 'minValue' && (
              <span className={css.tooltip}>
                <FormattedMessage id={minError.id} values={minError.values} />
              </span>
            )}
          </div>
          <span className={css.valueSeparator}>-</span>
          <div className={css.inputWithTooltip}>
            <input
              className={classNames(css.maxValue, {
                [css.valueInSidebar]: isInSideBar,
                [css.inputError]: !!maxError,
              })}
              type="number"
              step="any"
              placeholder={defaultMaxValue}
              value={fieldValues.maxValue}
              onChange={handleMaxValueChange}
            ></input>
            {maxError && activeField === 'maxValue' && (
              <span className={css.tooltip}>
                <FormattedMessage id={maxError.id} values={maxError.values} />
              </span>
            )}
          </div>
        </div>
      </div>
      <div className={css.sliderWrapper}>
        <RangeSlider
          min={defaultMinValue}
          max={defaultMaxValue}
          step={step}
          handles={sliderHandles}
          onChange={handles => {
            handleSliderChange({ minValue: handles[0], maxValue: handles[1] });
          }}
        />
      </div>
    </div>
  );
};

const FieldSelectIntegerRange = props => {
  const { max, min, name, step, isInSideBar, ...rest } = props;
  return (
    <Field
      max={max}
      min={min}
      name={name}
      step={step}
      isInSideBar={isInSideBar}
      component={RangeInput}
      {...rest}
    />
  );
};

FieldSelectIntegerRange.defaultProps = {
  min: null,
  max: null,
  step: 5,
  name: null,
  isInSideBar: false,
  onValidityChange: null,
  onValuesChange: null,
};

FieldSelectIntegerRange.propTypes = {
  min: number,
  max: number,
  step: number,
  isInSideBar: bool.isRequired,
  name: string,
  onValidityChange: func,
  onValuesChange: func,
};

export default FieldSelectIntegerRange;
